<template>
  <div class="provider-pie-chart">
    <div v-if="loading" class="chart-loading">
      <el-icon class="is-loading"><i class="el-icon-loading"></i></el-icon>
      <span>数据加载中...</span>
    </div>
    <div v-else-if="!hasData" class="chart-empty">
      <i class="el-icon-pie-chart"></i>
      <span>暂无数据</span>
    </div>
    <div v-else ref="chart" class="chart-container"></div>
  </div>
</template>

<script>
import echarts from 'echarts'

export default {
  name: 'provider-pie-chart',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      chart: null,
      resizeHandler: null
    }
  },

  computed: {
    hasData() {
      return this.data && this.data.length > 0
    },

    chartData() {
      if (!this.hasData) return []

      // 过滤掉使用次数为0的服务商,并按从大到小排序
      const filteredData = this.data.filter(item =>
        item.times && Number(item.times) > 0
      ).map(item => ({
        name: item.name,
        value: Number(item.times),
        fee: Number(item.fee || 0)
      }))
      .sort((a, b) => b.value - a.value)

      console.log('饼图原始数据:', this.data)
      console.log('饼图过滤后数据:', filteredData)

      return filteredData
    }
  },

  watch: {
    data: {
      handler() {
        this.$nextTick(() => {
          if (this.hasData && !this.chart) {
            this.initChart()
          } else if (this.chart) {
            this.renderChart()
          }
        })
      },
      deep: true
    },

    loading(newVal) {
      if (!newVal && this.hasData) {
        this.$nextTick(() => {
          if (!this.chart) {
            this.initChart()
          } else {
            this.renderChart()
          }
        })
      }
    }
  },

  mounted() {
    this.initChart()
    this.bindEvents()
  },

  beforeDestroy() {
    this.unbindEvents()
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },

  methods: {
    /**
     * 初始化图表
     */
    initChart() {
      console.log('饼图初始化开始:', {
        hasRef: !!this.$refs.chart,
        hasEcharts: typeof echarts !== 'undefined',
        refElement: this.$refs.chart
      })

      if (!this.$refs.chart) {
        console.log('饼图初始化失败: DOM引用不存在')
        return
      }

      if (typeof echarts === 'undefined') {
        console.log('饼图初始化失败: ECharts未定义')
        return
      }

      try {
        this.chart = echarts.init(this.$refs.chart)
        console.log('饼图实例创建成功:', !!this.chart)
        this.renderChart()
      } catch (error) {
        console.error('饼图初始化错误:', error)
      }
    },

    /**
     * 渲染图表
     */
    renderChart() {
      console.log('服务商饼图渲染开始:', {
        hasChart: !!this.chart,
        hasData: this.hasData,
        dataLength: this.data ? this.data.length : 0,
        chartData: this.chartData
      })

      if (!this.hasData) {
        console.log('服务商饼图渲染终止: 无数据')
        // 如果没有数据，销毁已有图表实例
        if (this.chart) {
          this.chart.dispose()
          this.chart = null
        }
        return
      }

      if (!this.chart) {
        console.log('服务商饼图渲染终止: 图表实例不存在')
        return
      }

      // 颜色映射：确保每个服务商始终使用相同的颜色
      const colorMap = {
        '阿里云': '#667eea',   // 紫蓝色
        '腾讯云': '#5daf7f',   // 墨绿色
        '优刻得': '#fa7c91'    // 粉红色
      }

      // 计算图例高度,合理利用空间
      const chartHeight = 320
      const itemHeight = 14
      const itemGap = 12
      const legendPadding = 80 // 考虑到有标题,留更多空间
      const maxLegendItems = Math.floor((chartHeight - legendPadding) / (itemHeight + itemGap))

      const option = {
        title: {
          text: '服务商使用分布',
          left: 'center',
          top: 20,
          textStyle: {
            fontSize: 16,
            color: '#333'
          }
        },
        tooltip: {
          trigger: 'item',
          formatter: (params) => {
            const { name, value, data } = params
            return `
              <div>
                <strong>${name}</strong><br/>
                调用次数: <span style="color: #409EFF">${this.formatNumber(value)}</span><br/>
                费用: <span style="color: #E6A23C">¥${this.formatMoney(data.fee)}</span><br/>
                占比: <span style="color: #67C23A">${params.percent}%</span>
              </div>
            `
          }
        },
        legend: {
          type: this.chartData.length > maxLegendItems ? 'scroll' : 'plain',
          orient: 'vertical',
          right: 20,
          top: 'center',
          bottom: 20,
          itemWidth: 14,
          itemHeight: 14,
          itemGap: 12,
          textStyle: {
            fontSize: 12,
            color: '#666'
          },
          pageButtonItemGap: 10,
          pageButtonPosition: 'end',
          pageFormatter: '{current}/{total}',
          pageIconColor: '#667eea',
          pageIconInactiveColor: '#aaa',
          pageIconSize: 12,
          pageTextStyle: {
            color: '#666',
            fontSize: 11
          },
          pageButtonGap: 5,
          formatter: (name) => {
            const item = this.chartData.find(d => d.name === name)
            if (item) {
              const total = this.chartData.reduce((sum, d) => sum + d.value, 0)
              const percent = ((item.value / total) * 100).toFixed(1)
              return `${name}  ${percent}%`
            }
            return name
          }
        },
        series: [
          {
            name: '服务商使用量',
            type: 'pie',
            radius: ['45%', '70%'],
            center: ['35%', '50%'],
            avoidLabelOverlap: true,
            itemStyle: {
              borderRadius: 8,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: true,
              position: 'outside',
              formatter: '{d}%',
              fontSize: 12,
              color: '#666',
              fontWeight: 'bold'
            },
            labelLine: {
              show: true,
              length: 15,
              length2: 10,
              smooth: true,
              lineStyle: {
                color: '#999',
                width: 1
              }
            },
            emphasis: {
              label: {
                show: true,
                fontSize: '16',
                fontWeight: 'bold'
              },
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              },
              scale: true,
              scaleSize: 5
            },
            data: this.chartData.map(item => ({
              name: item.name,
              value: item.value,
              fee: item.fee,
              itemStyle: {
                color: colorMap[item.name] || '#667eea'
              }
            })),
            animationType: 'scale',
            animationEasing: 'elasticOut',
            animationDelay: function (idx) {
              return Math.random() * 200
            }
          }
        ],
        color: Object.values(colorMap)
      }

      this.chart.setOption(option, true)
    },

    /**
     * 绑定事件
     */
    bindEvents() {
      this.resizeHandler = () => {
        if (this.chart) {
          this.chart.resize()
        }
      }
      window.addEventListener('resize', this.resizeHandler)
    },

    /**
     * 解绑事件
     */
    unbindEvents() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }
    },

    /**
     * 格式化数字
     */
    formatNumber(num) {
      if (!num) return '0'
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    /**
     * 格式化金额
     */
    formatMoney(amount) {
      if (!amount) return '0.00'
      return Number(amount).toFixed(2)
    }
  }
}
</script>

<style scoped>
.provider-pie-chart {
  height: 100%;
  position: relative;
  background: linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%);
  border-radius: 12px;
  padding: 8px;
}

.chart-container {
  width: 100%;
  height: 100%;
  min-height: 350px;
  animation: chartReveal 0.8s ease-out;
}

@keyframes chartReveal {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.chart-loading,
.chart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 320px;
  color: #909399;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.8) 0%, rgba(250, 250, 250, 0.9) 100%);
  border-radius: 8px;
}

.chart-loading {
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

.chart-loading .el-icon {
  font-size: 32px;
  margin-bottom: 12px;
  color: #667eea;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.chart-empty i {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.3;
  color: #667eea;
  animation: bounce 2s ease-in-out infinite;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.chart-empty span {
  font-size: 15px;
  color: #909399;
  font-weight: 500;
}
</style>



// WEBPACK FOOTER //
// src/custom/components/stats/message-stats/components/provider-pie-chart.vue